import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Input, Message, Button, FormToggle } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Eye, EyeOff } from 'lucide-react';
import { useSelector } from 'react-redux';

import SideModal from 'components/atoms/sideModal';
import { activeNetworkSelector, userSelector } from '../../../redux/selectors';
import SharedListLink from './sharedListLink';
import IntroRequest from './introRequest';
import LayoutSelector from './layoutSelector';
import { FieldVisibilitySelector } from './components/fieldVisibilitySelector';
import { useFocusTrapState } from '../../../hooks/useFocusTrapState';
import ContactsSharedListSchema from '../../../schemas/contactsSharedList';
import { EnableSharing } from './enableSharing';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { getContactsViewKey } from '../../../helpers/storeKeys';
import { useFieldVisibility } from './hooks/useFieldVisibility';
import { useSharedView } from './hooks/useSharedView';
import { usePasswordProtection } from './hooks/usePasswordProtection';
import { useIntroRequest } from './hooks/useIntroRequest';

export const ShareSharedListModal = ({ list, onClose }) => {
  const { pauseFocusTrap, onDropDownStateChange } = useFocusTrapState();
  const activeNetwork = useSelector(activeNetworkSelector);
  const user = useSelector(userSelector);
  const viewKey = getContactsViewKey(activeNetwork, list?.id, false);
  const [currentView] = useLocalStorage(viewKey, 'table');

  const {
    sharedView,
    viewId,
    isShared,
    enableSharing,
    disableSharing,
    onLayoutChange,
    updateView,
    isCreatingView,
    isUpdatingView,
    isDeletingView,
  } = useSharedView({
    list,
    activeNetwork,
    currentView,
    user,
  });

  const { availableFields, fieldsState, onFieldsChange, getInitialFieldsSerialized, getAllFieldsSerialized } =
    useFieldVisibility({
      list,
      activeNetwork,
      updateView,
      viewId,
    });

  const {
    pwdVisible,
    setPwdVisible,
    pwdValue,
    setPwdValue,
    passwordChanged,
    setPasswordChanged,
    showPassword,
    onPasswordEnabled,
    onPasswordChange,
  } = usePasswordProtection({ sharedView, viewId, list, activeNetwork, updateView });

  const { onIntroRequestManagerChange, onShowIntroRequestChange } = useIntroRequest({
    sharedView,
    viewId,
    list,
    activeNetwork,
    user,
    updateView,
  });

  const onCloseModal = () => {
    setPwdValue('');
    onClose();
  };

  const handleEnableSharing = useCallback(() => {
    enableSharing(getInitialFieldsSerialized);
  }, [enableSharing, getInitialFieldsSerialized]);

  const handleLayoutChange = useCallback(
    (newLayout) => {
      const layout = typeof newLayout === 'string' ? newLayout : newLayout?.value;
      const fieldSettings = layout === 'cards_view' && getAllFieldsSerialized ? getAllFieldsSerialized() : undefined;
      onLayoutChange(layout, fieldSettings);
    },
    [onLayoutChange, getAllFieldsSerialized],
  );

  return (
    <SideModal width="680px" onClose={onCloseModal} isOpen title="Share list" pauseFocusTrap={pauseFocusTrap}>
      {!isShared && (
        <EnableSharing listName={list.name} onEnableSharing={handleEnableSharing} isLoading={isCreatingView} />
      )}
      {isShared && (
        <Box sx={{ borderRadius: '4px', mt: '32px' }}>
          <Flex sx={{ gap: '32px', flexDirection: 'column' }}>
            <Flex sx={{ gap: '8px', flexDirection: 'column' }}>
              <Text
                color="text.subtle"
                fontSize="12px"
                fontWeight="400"
                lineHeight="16px"
                letterSpacing="1px"
                sx={{ textTransform: 'uppercase' }}
              >
                Shared view URL
              </Text>
              <SharedListLink
                listUrl={sharedView?.slug ? `${window.location.origin}/shared-lists/${sharedView.slug}` : ''}
              />
              <Flex sx={{ mt: '8px', gap: '16px', flexDirection: 'column' }}>
                <FormToggle
                  label="Password protection"
                  onChange={(e) => onPasswordChange(e)}
                  helpText="Require a password for visitors to view the list."
                  field={{
                    id: 'pwd-protection',
                    name: 'pwd-protection',
                    value: showPassword,
                  }}
                  form={{
                    setFieldValue: (_, value) => onPasswordChange({ target: { checked: value } }),
                  }}
                />
                {showPassword && (
                  <Box>
                    <Flex sx={{ gap: '8px' }}>
                      <Box sx={{ position: 'relative', flexGrow: '1' }}>
                        <Input
                          type={pwdVisible ? 'text' : 'password'}
                          value={pwdValue}
                          placeholder="Enter password"
                          onChange={(event) => {
                            setPasswordChanged(false);
                            setPwdValue(event.target.value);
                          }}
                          data-testid="password"
                        />
                        <Box
                          onClick={() => setPwdVisible((current) => !current)}
                          sx={{
                            position: 'absolute',
                            top: '0',
                            bottom: '0',
                            right: '16px',
                            height: '16px',
                            margin: 'auto',
                            cursor: 'pointer',
                          }}
                        >
                          <Box as={pwdVisible ? Eye : EyeOff} width="16px" height="16px" aria-hidden="true" />
                        </Box>
                      </Box>
                      <Box maxWidth="87px">
                        <Button
                          variant="primary"
                          disabled={!pwdValue || passwordChanged}
                          data-testid="button-save"
                          loading={isUpdatingView}
                          type="submit"
                          onClick={onPasswordEnabled}
                        >
                          Save
                        </Button>
                      </Box>
                    </Flex>
                  </Box>
                )}
              </Flex>
            </Flex>
            <Flex sx={{ gap: '8px', flexDirection: 'column' }}>
              <Text
                color="text.subtle"
                fontSize="12px"
                fontWeight="400"
                lineHeight="16px"
                letterSpacing="1px"
                sx={{ textTransform: 'uppercase' }}
              >
                Intros
              </Text>
              <IntroRequest
                value={sharedView?.introRequestManager?.id || user.id}
                onChange={onIntroRequestManagerChange}
                onDropDownStateChange={onDropDownStateChange}
                showIntroRequest={sharedView?.introRequestsEnabled || false}
                onShowIntroRequestChange={onShowIntroRequestChange}
              />
            </Flex>
            <Flex sx={{ gap: '8px', flexDirection: 'column' }}>
              <Text
                color="text.subtle"
                fontSize="12px"
                fontWeight="400"
                lineHeight="16px"
                letterSpacing="1px"
                sx={{ textTransform: 'uppercase' }}
              >
                Display settings
              </Text>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <LayoutSelector
                  value={sharedView?.type}
                  onChange={handleLayoutChange}
                  onDropDownStateChange={onDropDownStateChange}
                  currentListView={currentView}
                />
                <FieldVisibilitySelector
                  availableFields={availableFields}
                  fieldsState={fieldsState}
                  onChange={onFieldsChange}
                  onDropDownStateChange={onDropDownStateChange}
                  disabled={sharedView?.type === 'cards_view'}
                />
              </div>
            </Flex>
          </Flex>
          {sharedView?.passwordProtected && pwdValue && !passwordChanged && (
            <Box mt="8px">
              <Message content="The old password won’t work anymore after updating it." />
            </Box>
          )}
        </Box>
      )}

      {isShared && viewId && (
        <Box>
          <Box sx={{ height: '1px', backgroundColor: 'border.subtle', width: '100%', my: '24px' }} />
          <Button
            variant="secondary"
            size="small"
            loading={isDeletingView}
            onClick={async () => {
              try {
                await disableSharing();
                onClose();
              } catch (error) {
                // Error toast already shown by disableSharing hook
              }
            }}
          >
            Disable shared view
          </Button>
        </Box>
      )}
    </SideModal>
  );
};

ShareSharedListModal.propTypes = {
  list: ContactsSharedListSchema.isRequired,
  onClose: PropTypes.func.isRequired,
};
